import React, { useState, useEffect } from 'react';
import { API_BASE } from './index';

// ── Orders: status workflow config ──
const STATUS_CONFIG = {
  PENDING_PAYMENT: { label: 'Pending Payment', color: '#888',    bg: '#f5f5f5',
                     next: 'FULFILLMENT', nextLabel: '▶ Move to Fulfilment' },
  FULFILLMENT:     { label: 'Fulfillment',     color: '#e67e22', bg: '#fef3e2',
                     next: 'COMPLETED',   nextLabel: '✅ Mark Completed' },
  COMPLETED:       { label: 'Completed',       color: '#27ae60', bg: '#eafaf1',
                     next: null,          nextLabel: null },
  CANCELLED:       { label: 'Cancelled',       color: '#e74c3c', bg: '#fdecea',
                     next: null,          nextLabel: null },
};

const DEFAULT_ORDER_FILTERS = {
  status: 'ALL',           // ALL | PENDING_PAYMENT | FULFILLMENT | COMPLETED | CANCELLED
  fulfillmentType: 'ALL',  // ALL | local | shipping
  dateFrom: '',
  dateTo: '',
  search: '',
  sort: 'date_desc',
};

// ── Size config parsing: mirrors backend parseBatchToSizesFrom() ──
const SIZE_LETTER_TO_NAME = { S: 'SMALL', L: 'LARGE', K: 'BALL' };
const SIZE_DISPLAY_ORDER = [['SMALL', 'Small'], ['LARGE', 'Large'], ['BALL', 'Ball']];

function parseSizeConfigString(raw) {
  const result = {};
  if (!raw) return result;
  for (const entry of raw.split(',')) {
    const parts = entry.trim().split('_');
    if (parts.length < 3) continue;
    const sizeName = SIZE_LETTER_TO_NAME[parts[0].toUpperCase()] ?? parts[0].toUpperCase();
    const batchSize = parseFloat(parts[1]);
    const step = parseInt(parts[2], 10);
    if (!batchSize || Number.isNaN(batchSize)) continue; // zero/invalid batchSize → excluded, same as backend
    result[sizeName] = { batchSize, step: Number.isNaN(step) ? null : step };
  }
  return result;
}

// Size Config Override field + live per-unit price preview, shared by the
// Add Product and Edit Product forms. unitPrice(size) = price / batchSize(size)
// — same formula as BakeryController.placeOrder / StorefrontApp.calcUnitPrice.
function SizeConfigOverrideField({ price, sizeConfig, onChange, globalRaw }) {
  const hasOverride = !!(sizeConfig && sizeConfig.trim());
  const effectiveConfig = parseSizeConfigString(hasOverride ? sizeConfig : globalRaw);
  const numericPrice = parseFloat(price);

  return (
    <div>
      <label className="label-caps">Size Config Override</label>
      <input
        type="text"
        value={sizeConfig ?? ''}
        onChange={e => onChange(e.target.value || null)}
        placeholder={globalRaw || 'e.g. S_30_5,L_15_3,K_0_0'}
        className="admin-input"
      />
      <p style={{ margin: '4px 0 6px', fontSize: '12px', color: '#999' }}>
        {hasOverride
          ? 'Overriding the global default for this product.'
          : globalRaw
            ? `Blank — using the current global default: ${globalRaw}`
            : 'Blank — using the global default (loading...)'}
      </p>
      <div style={{ margin: '4px 0 10px', padding: '8px 10px', background: '#faf8f5', borderRadius: '6px' }}>
        <label className="label-caps" style={{ fontSize: '11px' }}>Per-Unit Price Preview</label>
        {Number.isNaN(numericPrice) ? (
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#999' }}>Enter a price above to preview per-size pricing.</p>
        ) : (
          <div style={{ display: 'flex', gap: '18px', marginTop: '4px', fontSize: '13px' }}>
            {SIZE_DISPLAY_ORDER.map(([key, label]) => {
              const cfg = effectiveConfig[key];
              return (
                <span key={key}>
                  <strong>{label}:</strong>{' '}
                  {cfg
                    ? `$${(Math.round((numericPrice / cfg.batchSize) * 100) / 100).toFixed(2)}/pc`
                    : <em style={{ color: '#bbb' }}>N/A</em>}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const ProductRow = React.memo(({ p, uploadingImageFor, onEdit, onDelete, onImageUpload }) => {
	const PLACEHOLDER = 'https://storage.googleapis.com/rugelach-images/placeholder.svg';
	return (
	<div className="product-row">
	  <img
		src={p.imageUrl || PLACEHOLDER}
		alt={p.name}
		className="product-row__thumb"
		onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER; }}
	  />
	  <div className="product-row__info">
		<h4 className="product-row__name">{p.name}</h4>
		<div className="product-row__price-row">
		  <span className="product-row__price">${p.price?.toFixed(2)}</span>
		  <span className="product-category-tag">{p.category || 'Uncategorized'}</span>
		</div>
	  </div>
	  <div className="product-row__actions">
		<div className="product-row__actions-row">
		  <button onClick={() => onEdit(p)} className="btn-admin-ghost">Edit</button>
		  <button onClick={() => onDelete(p.id)} className="btn-admin-danger">Delete</button>
		</div>
		<label className="product-row__upload-label">
		  {uploadingImageFor === p.id ? 'Uploading...' : '📸 Upload image'}
		  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onImageUpload(e.target.files[0], p.id)} />
		</label>
	  </div>
	</div>
	);
});

export default function Admin({ uiMode, setUiMode, products, setProducts, initialOrderFilter }) {
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [mfaCountdown, setMfaCountdown] = useState(60);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [activeTab, setActiveTab] = useState('products');

  // ── Products ──
  const PRODUCT_CATEGORIES = ['Rugalach', 'Cookies', 'Specials'];
  const [newItem, setNewItem] = useState({ name: '', description: '', price: '', imageUrl: '', category: 'Rugalach', sizeConfig: null });
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImageFor, setUploadingImageFor] = useState(null); // product id or 'new'
  const [imageUploadError, setImageUploadError] = useState('');
  const [productSearch, setProductSearch] = useState('');

  // ── Customizations ──
  const [allCustomizations, setAllCustomizations] = useState([]);
  const [newCustomization, setNewCustomization] = useState({ name: '', description: '', type: 'TOPPING', priceDelta: '', itemIds: [] });
  const [editingCustomization, setEditingCustomization] = useState(null);

  // ── Capacity dashboard ──
  const [daysSummary, setDaysSummary] = useState([]);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [overrideInputs, setOverrideInputs] = useState({});
  const [blockModal, setBlockModal] = useState(null); // date string when open
  const [blockForm, setBlockForm] = useState({ label: '', batchesBlocked: 1 });
  const [savingOverride, setSavingOverride] = useState({});

  // ── Settings tab ──
  const [appSettings, setAppSettings] = useState([]);
  const [settingsEdits, setSettingsEdits] = useState({});

  // ── Coupons tab ──
  const [allCoupons, setAllCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({
    code: '', discountType: 'PERCENTAGE', discountValue: '', minOrderAmount: '',
    maxDiscountAmount: '', maxUses: '', singleUsePerEmail: true, active: true, expiresAt: ''
  });
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponRedemptions, setCouponRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  const [expandedCouponId, setExpandedCouponId] = useState(null);

  // ── Orders tab ──
  const [allOrders, setAllOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false);

  // Filters
  const [orderFilters, setOrderFilters] = useState(DEFAULT_ORDER_FILTERS);
  const [searchInput, setSearchInput] = useState(''); // debounced into orderFilters.search

  // Pagination
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalCount, setOrdersTotalCount] = useState(0);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const ORDERS_PAGE_SIZE = 20;

  // Expand/line items
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orderItemsById, setOrderItemsById] = useState({});       // { [orderId]: items[] }
  const [orderItemsLoading, setOrderItemsLoading] = useState({}); // { [orderId]: bool }

  // Status updates / delete
  const [statusUpdating, setStatusUpdating] = useState({}); // { [orderId]: bool }
  const [orderDeleting, setOrderDeleting] = useState({});   // { [orderId]: bool }  
  
  useEffect(() => {
    const t = setTimeout(() => {
	  setOrderFilters(prev => ({ ...prev, search: searchInput }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);
  
  useEffect(() => {
	if (!isAuthenticated) return;
	fetchAllOrders(adminToken, { page: 1, append: false });
    setExpandedOrderId(null);
  }, [orderFilters, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && (activeTab === 'settings' || activeTab === 'products')) fetchSettings(adminToken);
  }, [isAuthenticated, activeTab]);

  const batchToSizesRaw = appSettings.find(s => s.settingKey === 'BATCH_TO_SIZES_KEY')?.settingValue ?? '';
  
  // ─────────────────────────────────────────
  // Auth timers
  // ─────────────────────────────────────────
  useEffect(() => {
    if (uiMode !== 'admin-mfa' || mfaCountdown <= 0) return;
    const timer = setTimeout(() => setMfaCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [uiMode, mfaCountdown]);

  // ─────────────────────────────────────────
  // Data fetchers
  // ─────────────────────────────────────────
  const fetchAllCustomizations = async (token) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/customizations`, {
        headers: { 'Authorization': `Bearer ${token || adminToken}` }
      });
      const all = await res.json();
      // Fetch assignments for each customization
      const withAssignments = await Promise.all(all.map(async c => {
        try {
          const aRes = await fetch(`${API_BASE}/api/admin/customizations/${c.id}/assignments`, {
            headers: { 'Authorization': `Bearer ${token || adminToken}` }
          });
          const itemIds = aRes.ok ? await aRes.json() : [];
          return { ...c, itemIds };
        } catch { return { ...c, itemIds: [] }; }
      }));
      withAssignments.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'PRODUCT' ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      setAllCustomizations(withAssignments);
    } catch (e) { console.error(e); }
  };

  const fetchDaysSummary = async (token) => {
    setCapacityLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      // 16 days (today .. today+15) so this view always covers every date a customer
      // can see in the picker, including the "after 2 PM EST skips tomorrow" edge case
      // where the customer window can reach today+15.
      const res = await fetch(`${API_BASE}/api/admin/days-summary?from=${today}&days=16`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setDaysSummary(data);
      const inputs = {};
      data.forEach(d => { if (d.overriddenCapacity != null) inputs[d.date] = d.overriddenCapacity; });
      setOverrideInputs(inputs);
    } catch (e) { console.error(e); }
    setCapacityLoading(false);
  };

  const fetchAllCoupons = async (token) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/coupons`, {
        headers: { 'Authorization': `Bearer ${token || adminToken}` }
      });
      const data = await res.json();
      setAllCoupons(data);
    } catch (e) { console.error(e); }
  };

  const fetchAllOrders = async (token, { page = 1, append = false } = {}) => {
    if (append) setOrdersLoadingMore(true); else setOrdersLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(ORDERS_PAGE_SIZE),
        sort: orderFilters.sort,
      });
      if (orderFilters.status !== 'ALL') params.set('status', orderFilters.status);
      if (orderFilters.fulfillmentType !== 'ALL') params.set('fulfillmentType', orderFilters.fulfillmentType);
      if (orderFilters.dateFrom) params.set('dateFrom', orderFilters.dateFrom);
      if (orderFilters.dateTo) params.set('dateTo', orderFilters.dateTo);
      if (orderFilters.search.trim()) params.set('search', orderFilters.search.trim());

      const res = await fetch(`${API_BASE}/api/admin/orders?${params.toString()}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAllOrders(prev => append ? [...prev, ...data.orders] : data.orders);
      setOrdersTotalCount(data.totalCount);
      setOrdersHasMore(data.hasMore);
      setOrdersPage(data.page);
    } catch (e) { console.error(e); }
    setOrdersLoading(false);
    setOrdersLoadingMore(false);
  };

  // ─────────────────────────────────────────
  // Auth
  // ─────────────────────────────────────────
  const executeAdminLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminEmail, password: adminPassword })
      });
      const data = await response.json();
      if (response.ok && data.status === 'MFA_REQUIRED') {
        setUiMode('admin-mfa');
        setMfaCountdown(60);
      } else {
        setAuthError(data.message || 'Invalid administrative credentials.');
      }
    } catch (err) {
      setAuthError('Unable to reach authentication backend: ' + err.message);
    }
  };

  const executeMfaVerification = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await fetch(`${API_BASE}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminEmail, code: twoFactorCode })
      });
      const data = await response.json();
      if (response.ok && data.status === 'SUCCESS') {
        setAdminToken(data.token);
		setIsAuthenticated(true);
		setUiMode('admin-dashboard');
		fetchAllCustomizations(data.token);
		fetchDaysSummary(data.token);
		fetchAllCoupons(data.token);
		if (initialOrderFilter) {
		  setActiveTab('orders');
		  setSearchInput(initialOrderFilter);
		}
      } else {
        setAuthError(data.message || 'Invalid or expired authentication code.');
      }
    } catch (err) {
      setAuthError('Verification network failure: ' + err.message);
    }
  };
  
  const fetchOrderItems = async (orderId) => {
    if (orderItemsById[orderId]) return;
    setOrderItemsLoading(p => ({ ...p, [orderId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/orders/${orderId}`, {
        headers: { 'Content-Type': 'application/json',  'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      setOrderItemsById(p => ({ ...p, [orderId]: data.items || [] }));
    } catch (e) { console.error(e); }
    setOrderItemsLoading(p => ({ ...p, [orderId]: false }));
  };

  const toggleExpandOrder = (orderId) => {
    const next = expandedOrderId === orderId ? null : orderId;
    setExpandedOrderId(next);
    if (next) fetchOrderItems(next);
  };
  
  const handleUpdateOrderStatus = async (orderId, nextStatus) => {
    if (nextStatus === 'CANCELLED' && !window.confirm('Cancel this order? This will free up its reserved capacity.')) return;
    setStatusUpdating(p => ({ ...p, [orderId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ orderStatus: nextStatus })
      });
      if (res.ok) {
        const updated = await res.json();
        setAllOrders(prev => prev.map(o => o.id === orderId ? { ...o, orderStatus: updated.orderStatus } : o));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.message || 'Failed to update order status.');
      }
    } catch (e) { alert('Error: ' + e.message); }
    setStatusUpdating(p => ({ ...p, [orderId]: false }));
  };
  
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Permanently delete this order? This cannot be undone.')) return;
    setOrderDeleting(p => ({ ...p, [orderId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        setAllOrders(prev => prev.filter(o => o.id !== orderId));
        setOrdersTotalCount(c => Math.max(0, c - 1));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.message || 'Failed to delete order.');
      }
    } catch (e) { alert('Error: ' + e.message); }
    setOrderDeleting(p => ({ ...p, [orderId]: false }));
  };
  
  const handleLoadMoreOrders = () => {
    fetchAllOrders(adminToken, { page: ordersPage + 1, append: true });
  };

  const clearOrderFilters = () => {
    setOrderFilters(DEFAULT_ORDER_FILTERS);
    setSearchInput('');
  };

  const hasActiveOrderFilters =
    Object.keys(DEFAULT_ORDER_FILTERS).some(key => orderFilters[key] !== DEFAULT_ORDER_FILTERS[key]) ||
    searchInput !== '';

  // ─────────────────────────────────────────
  // Products
  // ─────────────────────────────────────────
  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/api/admin/products/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          name: newItem.name,
          description: newItem.description,
          price: parseFloat(newItem.price),
		  category: newItem.category,
          isNew: true
        })
      });
      if (response.ok) {
        const addedProduct = await response.json();
        setProducts((prev) => [...prev, addedProduct]);
        setNewItem({ name: '', description: '', price: '', imageUrl: '', category: 'Rugalach', sizeConfig: null });
        alert("✨ Success! New item saved.");
      } else if (response.status === 401) {
        alert("❌ Unauthorized.");
      } else {
        alert("Failed to save product.");
      }
    } catch (err) { alert("Error: " + err.message); }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/api/admin/products/edit/${editingProduct.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(editingProduct)
      });
      if (response.ok) {
        const updated = await response.json();
        setProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
        setEditingProduct(null);
        alert("✨ Product updated!");
      }
    } catch (err) { alert("Error: " + err.message); }
  };

  const handleDeleteProduct = React.useCallback(async (id) => {
	  if (!window.confirm("⚠️ Delete this product?")) return;
	  try {
		const response = await fetch(`${API_BASE}/api/admin/products/delete/${id}`, {
		  method: 'DELETE',
		  headers: { 'Authorization': `Bearer ${adminToken}` }
		});
		if (response.ok) {
		  setProducts(prev => prev.filter(p => p.id !== id));
		  alert("🗑️ Deleted.");
		}
	  } catch (err) { alert("Error: " + err.message); }
  }, [adminToken]);
  
  const handleImageUpload = React.useCallback(async (file, itemId) => {
	  if (!file) return;
	  setUploadingImageFor(itemId);
	  setImageUploadError('');
	  try {
		const formData = new FormData();
		formData.append('file', file);
		const res = await fetch(`${API_BASE}/api/admin/items/${itemId}/image`, {
		  method: 'POST',
		  headers: { 'Authorization': `Bearer ${adminToken}` },
		  body: formData,
		});
		if (res.ok) {
		  const data = await res.json();
		  setProducts(prev => prev.map(p => p.id === itemId ? { ...p, imageUrl: data.imageUrl } : p));
		  setEditingProduct(prev => prev?.id === itemId ? { ...prev, imageUrl: data.imageUrl } : prev);
		  alert('✅ Image uploaded!');
		} else {
		  setImageUploadError('Upload failed. Please try again.');
		}
	  } catch (err) {
		setImageUploadError('Upload error: ' + err.message);
	  }
	  setUploadingImageFor(null);
  }, [adminToken]);

  const handleRemoveImage = React.useCallback(async () => {
	  if (!window.confirm('Remove this product image?')) return;
	  try {
		const response = await fetch(`${API_BASE}/api/admin/items/${editingProduct.id}/image`, {
		  method: 'DELETE',
		  headers: { 'Authorization': `Bearer ${adminToken}` }
		});
		if (!response.ok) throw new Error('Failed to remove image');
		setProducts(prev => prev.map(p => p.id === editingProduct.id ? { ...p, imageUrl: null } : p));
		setEditingProduct(prev => prev ? { ...prev, imageUrl: null } : prev);
	  } catch (err) {
		alert(err.message || 'Failed to remove image');
	  }
  }, [adminToken, editingProduct]);

  // ─────────────────────────────────────────
  // Customizations
  // ─────────────────────────────────────────
  const handleAddCustomization = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/admin/customizations/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          name: newCustomization.name,
          description: newCustomization.description,
          type: newCustomization.type,
          priceDelta: parseFloat(newCustomization.priceDelta) || 0,
          itemIds: newCustomization.itemIds.length > 0 ? newCustomization.itemIds : null,
          active: true
        })
      });
      if (res.ok) {
        setNewCustomization({ name: '', description: '', type: 'TOPPING', priceDelta: '', itemIds: [] });
        fetchAllCustomizations();
      }
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleEditCustomization = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/admin/customizations/edit/${editingCustomization.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          name: editingCustomization.name,
          description: editingCustomization.description,
          type: editingCustomization.type,
          priceDelta: parseFloat(editingCustomization.priceDelta) || 0,
          active: editingCustomization.active,
          itemIds: editingCustomization.itemIds?.length > 0 ? editingCustomization.itemIds : null,
        })
      });
      if (res.ok) { setEditingCustomization(null); fetchAllCustomizations(); }
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleDeleteCustomization = async (id) => {
    if (!window.confirm('Delete this customization option?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/customizations/delete/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) fetchAllCustomizations();
    } catch (e) { alert('Error: ' + e.message); }
  };
  
  const toggleCustomizationForProduct = async (customization, productId) => {
    const currentIds = customization.itemIds || [];
    const isAssigned = currentIds.includes(productId);
    let newIds;
    if (currentIds.length === 0) {
      // Currently global — switching to specific: assign all products EXCEPT this one
      newIds = products.map(p => p.id).filter(id => id !== productId);
    } else if (isAssigned) {
      newIds = currentIds.filter(id => id !== productId);
      // If removing the last one, it becomes global again
      if (newIds.length === 0) newIds = [];
    } else {
      newIds = [...currentIds, productId];
    }
    try {
      await fetch(`${API_BASE}/api/admin/customizations/edit/${customization.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          name: customization.name,
          description: customization.description,
          type: customization.type,
          priceDelta: customization.priceDelta,
          active: customization.active,
          itemIds: newIds.length > 0 ? newIds : null,
        })
      });
      fetchAllCustomizations();
    } catch (e) { alert('Error: ' + e.message); }
  };

  // ─────────────────────────────────────────
  // Capacity overrides
  // ─────────────────────────────────────────
  const handleSaveOverride = async (date) => {
    const val = overrideInputs[date];
    if (val == null || val === '') return;
    setSavingOverride(p => ({ ...p, [date]: true }));
    try {
      await fetch(`${API_BASE}/api/admin/capacity-override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ date, batchOverride: parseInt(val) })
      });
      await fetchDaysSummary(adminToken);
    } catch (e) { alert('Error: ' + e.message); }
    setSavingOverride(p => ({ ...p, [date]: false }));
  };

  const handleRemoveOverride = async (date) => {
    try {
      await fetch(`${API_BASE}/api/admin/capacity-override/${date}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setOverrideInputs(p => { const n = {...p}; delete n[date]; return n; });
      await fetchDaysSummary(adminToken);
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleAddBlock = async () => {
    if (!blockForm.label.trim()) { alert('Please enter a label for this block.'); return; }
    try {
      await fetch(`${API_BASE}/api/admin/capacity-block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ date: blockModal, label: blockForm.label, batchesBlocked: parseInt(blockForm.batchesBlocked) })
      });
      setBlockModal(null);
      setBlockForm({ label: '', batchesBlocked: 1 });
      await fetchDaysSummary(adminToken);
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Remove this capacity block?')) return;
    try {
      await fetch(`${API_BASE}/api/admin/capacity-block/${blockId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      await fetchDaysSummary(adminToken);
    } catch (e) { alert('Error: ' + e.message); }
  };

  const fetchSettings = async (token) => {
    const res = await fetch(`${API_BASE}/api/admin/settings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      setAppSettings(data);
      const edits = {};
      data.forEach(s => { edits[s.settingKey] = s.settingValue; });
      setSettingsEdits(edits);
    }
  };

  const saveSetting = async (key, value) => {
    const settingValue = value !== undefined ? value : settingsEdits[key];
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value: settingValue })
      });
      if (res.ok) {
        alert('Setting saved.');
        fetchSettings(adminToken);
      } else {
        const msg = await res.text().catch(() => '');
        alert(msg ? `Failed to save setting: ${msg}` : 'Failed to save setting.');
      }
    } catch (e) { alert('Error: ' + e.message); }
  };

  // ─────────────────────────────────────────
  // Coupons
  // ─────────────────────────────────────────
  const buildCouponPayload = (coupon) => ({
    code: coupon.code.toUpperCase().trim(),
    discountType: coupon.discountType,
    discountValue: parseFloat(coupon.discountValue) || 0,
    minOrderAmount: coupon.minOrderAmount !== '' && coupon.minOrderAmount != null ? parseFloat(coupon.minOrderAmount) : null,
    maxDiscountAmount: coupon.discountType === 'PERCENTAGE' && coupon.maxDiscountAmount !== '' && coupon.maxDiscountAmount != null
      ? parseFloat(coupon.maxDiscountAmount) : null,
    maxUses: coupon.maxUses !== '' && coupon.maxUses != null ? parseInt(coupon.maxUses) : null,
    singleUsePerEmail: coupon.singleUsePerEmail,
    active: coupon.active,
    expiresAt: coupon.expiresAt || null
  });

  const handleAddCoupon = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/admin/coupons/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(buildCouponPayload(newCoupon))
      });
      if (res.ok) {
        const added = await res.json();
        setAllCoupons(prev => [...prev, added]);
        setNewCoupon({
          code: '', discountType: 'PERCENTAGE', discountValue: '', minOrderAmount: '',
          maxDiscountAmount: '', maxUses: '', singleUsePerEmail: true, active: true, expiresAt: ''
        });
        alert('✨ Coupon created!');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to create coupon.');
      }
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleEditCoupon = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/admin/coupons/edit/${editingCoupon.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(buildCouponPayload(editingCoupon))
      });
      if (res.ok) {
        const updated = await res.json();
        setAllCoupons(prev => prev.map(c => c.id === updated.id ? updated : c));
        setEditingCoupon(null);
        alert('✨ Coupon updated!');
      } else {
        alert('Failed to update coupon.');
      }
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/coupons/delete/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) setAllCoupons(prev => prev.filter(c => c.id !== id));
    } catch (e) { alert('Error: ' + e.message); }
  };

  const fetchCouponRedemptions = async (couponId) => {
    setRedemptionsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/coupons/${couponId}/redemptions`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      setCouponRedemptions(data);
    } catch (e) { console.error(e); setCouponRedemptions([]); }
    setRedemptionsLoading(false);
  };

  const toggleExpandCoupon = (couponId) => {
    const next = expandedCouponId === couponId ? null : couponId;
    setExpandedCouponId(next);
    if (next) fetchCouponRedemptions(next);
  };

  // ─────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────
  return (
    <>
      {/* LOGIN */}
      {uiMode === 'admin-login' && (
        <section className="admin-auth-screen">
          <h2 className="admin-auth__title">👨‍🍳 Kitchen Control Gateway</h2>
          <p className="admin-auth__subtitle">Enter your administrator credentials to receive your secure 2FA code.</p>
          {authError && <div className="auth-error">{authError}</div>}
          <form onSubmit={executeAdminLogin} className="admin-auth__form">
            <div>
              <label className="admin-auth__label">Admin Email Address</label>
              <input type="email" required value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="name@domain.com" className="admin-input" />
            </div>
            <div>
              <label className="admin-auth__label">Master Password</label>
              <input type="password" required value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="••••••••" className="admin-input" />
            </div>
            <button type="submit" className="admin-auth__submit">Request 2FA Token</button>
          </form>
          <button onClick={() => setUiMode('storefront')} className="admin-auth__link">Return to Storefront</button>
        </section>
      )}

      {/* MFA */}
      {uiMode === 'admin-mfa' && (
        <section className="admin-auth-screen">
          <h2 className="admin-auth__title">Security Verification</h2>
          <p className="admin-auth__subtitle">A secure login code has been sent via Resend to your administrative inbox.</p>
          <div className={`mfa-countdown${mfaCountdown <= 15 ? ' mfa-countdown--expiring' : ''}`}>
            <span className="mfa-countdown__num">{mfaCountdown}s</span>
          </div>
          {authError && <div className="auth-error">{authError}</div>}
          <form onSubmit={executeMfaVerification} className="admin-auth__form">
            <input autoFocus type="text" required maxLength="6" pattern="[0-9]{6}" placeholder="000000" value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value)} className="mfa-code-input" />
            <button type="submit" disabled={mfaCountdown <= 0} className="mfa-submit">
              {mfaCountdown <= 0 ? 'Token Expired' : 'Confirm Access Token'}
            </button>
          </form>
          <button onClick={() => { setUiMode('admin-login'); setAuthError(''); setTwoFactorCode(''); }} className="mfa-back-link">← Restart Login Session</button>
        </section>
      )}

      {/* DASHBOARD */}
      {uiMode === 'admin-dashboard' && isAuthenticated && (
        <section className="admin-dashboard">
          {/* Header */}
          <div className="admin-dashboard__header">
            <h2 className="admin-dashboard__title">Kitchen Console</h2>
            <button onClick={() => { setAdminToken(null); setIsAuthenticated(false); setUiMode('storefront'); }} className="admin-dashboard__logout">🔒 Logout & Exit</button>
          </div>

          {/* Tabs */}
          <div className="admin-tab-bar">
            {[
              { key: 'products', label: '📦 Products' },
              { key: 'customizations', label: '🎨 Customizations' },
              { key: 'capacity', label: '📅 Capacity' },
              { key: 'orders', label: '🧾 Orders' },
              { key: 'settings', label: '⚙️ Settings' },
              { key: 'coupons', label: '🏷️ Coupons' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`admin-tab${activeTab === tab.key ? ' admin-tab--active' : ''}`}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ══════════════════════════════════════════ */}
          {/* TAB: PRODUCTS                              */}
          {/* ══════════════════════════════════════════ */}
          {activeTab === 'products' && (
            <div className="products-grid">
              <div>
                {editingProduct ? (
                  <form onSubmit={handleEditSubmit} className="product-edit-form">
                    <h3 className="product-edit-form__title">✏️ Edit Product</h3>
                    <div><label className="label-caps">Name</label><input type="text" required value={editingProduct.name} onChange={e => setEditingProduct({...editingProduct, name: e.target.value})} className="admin-input" /></div>
                    <div><label className="label-caps">Description</label><textarea required rows="3" value={editingProduct.description} onChange={e => setEditingProduct({...editingProduct, description: e.target.value})} className="admin-input" /></div>
                    <div><label className="label-caps">Price ($)</label><input type="number" step="0.01" required value={editingProduct.price} onChange={e => setEditingProduct({...editingProduct, price: e.target.value})} className="admin-input" /></div>
                    <SizeConfigOverrideField
                      price={editingProduct.price}
                      sizeConfig={editingProduct.sizeConfig}
                      onChange={val => setEditingProduct({...editingProduct, sizeConfig: val})}
                      globalRaw={batchToSizesRaw}
                    />
                    <div>
					  <label className="label-caps">Product Image</label>
					  {editingProduct.imageUrl && (
						<>
						  <img src={editingProduct.imageUrl} alt="Current" className="product-edit__image" />
						  <button type="button" onClick={handleRemoveImage} className="btn-admin-danger">✕ Remove Image</button>
						</>
					  )}
					  <input
						type="file"
						accept="image/*"
						onChange={e => handleImageUpload(e.target.files[0], editingProduct.id)}
						className="admin-input"
					  />
					  {uploadingImageFor === editingProduct.id && <p className="product-uploading-msg">Uploading...</p>}
					  {imageUploadError && <p className="product-upload-error">{imageUploadError}</p>}
					</div>
                    <div><label className="label-caps">Category</label><select value={editingProduct.category || 'Rugalach'} onChange={e => setEditingProduct({...editingProduct, category: e.target.value})} className="admin-input">{PRODUCT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></div>
                    <div>
                      <label className="label-caps" style={{ display: 'block', marginBottom: '8px' }}>New Item</label>
                      <div className="radio-options-row">
                        <label className="radio-label"><input type="radio" name="isNew" checked={editingProduct.isNew === true} onChange={() => setEditingProduct({...editingProduct, isNew: true})} /> Yes</label>
                        <label className="radio-label"><input type="radio" name="isNew" checked={editingProduct.isNew === false} onChange={() => setEditingProduct({...editingProduct, isNew: false})} /> No</label>
                      </div>
                    </div>
					{allCustomizations.length > 0 && (
                    <div>
                      <label className="label-caps" style={{ display: 'block', marginBottom: '8px' }}>Customizations</label>
                      <div className="product-customizations-scroll">
                        {allCustomizations.map(c => {
                          const isGlobal = (c.itemIds || []).length === 0;
                          const applies = isGlobal || (c.itemIds || []).includes(editingProduct.id);
                          return (
                            <label key={c.id} className="product-customization-label">
                              <input
                                type="checkbox"
                                checked={applies}
                                onChange={() => toggleCustomizationForProduct(c, editingProduct.id)}
                              />
                              <span>{c.name}</span>
                              <span className={`badge-type badge-type--${c.type.toLowerCase()}`}>{c.type}</span>
                              {isGlobal && <span className="customization-applies-note">(all)</span>}
                              {c.priceDelta > 0 && <span className="customization-price-delta">+${c.priceDelta.toFixed(2)}</span>}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    )}
                    <div className="save-cancel-row">
                      <button type="submit" className="btn-save">Save Changes</button>
                      <button type="button" onClick={() => setEditingProduct(null)} className="btn-cancel">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleAdminSubmit} className="product-add-form">
                    <h3 className="product-edit-form__title">➕ Add New Product</h3>
                    <div><label className="label-caps">Name</label><input type="text" placeholder="e.g., Raspberry White Chocolate" required value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} className="admin-input" /></div>
                    <div><label className="label-caps">Description</label><textarea placeholder="Describe the product..." required rows="3" value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} className="admin-input" /></div>
                    <div><label className="label-caps">Price ($)</label><input type="number" step="0.01" placeholder="15.00" required value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} className="admin-input" /></div>
                    <SizeConfigOverrideField
                      price={newItem.price}
                      sizeConfig={newItem.sizeConfig}
                      onChange={val => setNewItem({...newItem, sizeConfig: val})}
                      globalRaw={batchToSizesRaw}
                    />
                    <div><label className="label-caps">Category</label><select value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} className="admin-input">{PRODUCT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></div>
                    <button type="submit" className="btn-admin-primary" style={{ width: '100%', padding: '12px' }}>Add to Menu 🚀</button>
                  </form>
                )}
              </div>
              <div className="products-list">
                <h3 className="products-list__title">Current Menu ({products.length})</h3>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="admin-input" style={{ marginBottom: '15px', fontSize: '13px' }}
                />
                <div className="products-list__items">
                  {products
                    .filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.category || '').toLowerCase().includes(productSearch.toLowerCase()))
                    .map((p) => (
                      <ProductRow
                        key={p.id}
                        p={p}
                        uploadingImageFor={uploadingImageFor}
                        onEdit={setEditingProduct}
                        onDelete={handleDeleteProduct}
                        onImageUpload={handleImageUpload}
                      />
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════ */}
          {/* TAB: CUSTOMIZATIONS                        */}
          {/* ══════════════════════════════════════════ */}
          {activeTab === 'customizations' && (
            <div className="customizations-grid">
              <div>
                {editingCustomization ? (
                  <form onSubmit={handleEditCustomization} className="customization-edit-form">
                    <h4 className="admin-section-heading">✏️ Edit Option</h4>
                    <div><label className="label-caps">Name</label><input type="text" required value={editingCustomization.name} onChange={e => setEditingCustomization({...editingCustomization, name: e.target.value})} className="admin-input" /></div>
                    <div><label className="label-caps">Description</label><input type="text" value={editingCustomization.description || ''} onChange={e => setEditingCustomization({...editingCustomization, description: e.target.value})} className="admin-input" /></div>
                    <div>
                      <label className="label-caps">Type</label>
                      <select value={editingCustomization.type} onChange={e => setEditingCustomization({...editingCustomization, type: e.target.value})} className="admin-input">
                        <option value="TOPPING">Topping</option>
                        <option value="PRODUCT">Product Customization</option>
                      </select>
                    </div>
                    <div><label className="label-caps">Price Delta ($)</label><input type="number" step="0.01" value={editingCustomization.priceDelta} onChange={e => setEditingCustomization({...editingCustomization, priceDelta: e.target.value})} className="admin-input" /></div>
                    <div>
                      <label className="label-caps">Applies to</label>
                      <div className="applies-to-list">
                        <label className={`applies-global-label${(editingCustomization.itemIds || []).length === 0 ? ' applies-global-label--active' : ''}`}>
                          <input type="checkbox" checked={(editingCustomization.itemIds || []).length === 0} onChange={() => setEditingCustomization({...editingCustomization, itemIds: []})} style={{ marginRight: '6px' }} />
                          All products
                        </label>
                        {products.map(p => (
                          <label key={p.id} className="applies-product-label">
                            <input type="checkbox" checked={(editingCustomization.itemIds || []).includes(p.id)} onChange={() => {
                              const ids = editingCustomization.itemIds || [];
                              const next = ids.includes(p.id) ? ids.filter(x => x !== p.id) : [...ids, p.id];
                              setEditingCustomization({...editingCustomization, itemIds: next});
                            }} style={{ marginRight: '6px' }} />
                            {p.name}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="save-cancel-row">
                      <button type="submit" className="btn-save">Save</button>
                      <button type="button" onClick={() => setEditingCustomization(null)} className="btn-cancel">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleAddCustomization} className="customization-add-form">
                    <h4 className="admin-section-heading">➕ Add Option</h4>
                    <div><label className="label-caps">Name</label><input type="text" required value={newCustomization.name} onChange={e => setNewCustomization({...newCustomization, name: e.target.value})} placeholder="e.g. Extra Sprinkles" className="admin-input" /></div>
                    <div><label className="label-caps">Description</label><input type="text" value={newCustomization.description || ''} onChange={e => setNewCustomization({...newCustomization, description: e.target.value})} className="admin-input" /></div>
                    <div>
                      <label className="label-caps">Type</label>
                      <select value={newCustomization.type} onChange={e => setNewCustomization({...newCustomization, type: e.target.value})} className="admin-input">
                        <option value="TOPPING">Topping</option>
                        <option value="PRODUCT">Product Customization</option>
                      </select>
                    </div>
                    <div><label className="label-caps">Price Delta ($)</label><input type="number" step="0.01" value={newCustomization.priceDelta} onChange={e => setNewCustomization({...newCustomization, priceDelta: e.target.value})} placeholder="0.00" className="admin-input" /></div>
                    <div>
                      <label className="label-caps">Applies to</label>
                      <div className="applies-to-list">
                        <label className={`applies-global-label${newCustomization.itemIds.length === 0 ? ' applies-global-label--active' : ''}`}>
                          <input type="checkbox" checked={newCustomization.itemIds.length === 0} onChange={() => setNewCustomization({...newCustomization, itemIds: []})} style={{ marginRight: '6px' }} />
                          All products
                        </label>
                        {products.map(p => (
                          <label key={p.id} className="applies-product-label">
                            <input type="checkbox" checked={newCustomization.itemIds.includes(p.id)} onChange={() => {
                              const next = newCustomization.itemIds.includes(p.id)
                                ? newCustomization.itemIds.filter(x => x !== p.id)
                                : [...newCustomization.itemIds, p.id];
                              setNewCustomization({...newCustomization, itemIds: next});
                            }} style={{ marginRight: '6px' }} />
                            {p.name}
                          </label>
                        ))}
                      </div>
                    </div>
                    <button type="submit" className="btn-admin-primary" style={{ width: '100%' }}>Add Option 🎨</button>
                  </form>
                )}
              </div>
              <div className="customizations-list">
                <h4 className="admin-section-heading" style={{ margin: '0 0 15px 0' }}>Current Options ({allCustomizations.length})</h4>
                <div className="customization-rows">
                  {allCustomizations.map(c => (
                    <div key={c.id} className="customization-row">
                      <div>
                        <span className="customization-row__name">{c.name}</span>
                        <span className={`badge-type badge-type--${c.type.toLowerCase()}`} style={{ marginLeft: '8px' }}>{c.type}</span>
                        {c.priceDelta > 0 && <span className="customization-price-delta">+${c.priceDelta.toFixed(2)}</span>}
                        <span className="customization-applies-note">
                          {(c.itemIds || []).length === 0 ? '(all)' : `(${c.itemIds.map(id => products.find(p => p.id === id)?.name || id).join(', ')})`}
                        </span>
                      </div>
                      <div className="product-row__actions-row">
                        <button onClick={() => setEditingCustomization(c)} className="btn-admin-ghost">Edit</button>
                        <button onClick={() => handleDeleteCustomization(c.id)} className="btn-admin-danger">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════ */}
          {/* TAB: CAPACITY                              */}
          {/* ══════════════════════════════════════════ */}
          {activeTab === 'capacity' && (
            <div>
              <div className="capacity-header">
                <h3 className="capacity-header__title">16-Day Capacity View</h3>
                <button onClick={() => fetchDaysSummary(adminToken)} className="btn-admin-primary" style={{ padding: '8px 16px' }}>↻ Refresh</button>
              </div>

              {capacityLoading ? (
                <p className="loading-text">Loading capacity data...</p>
              ) : (
                <div className="capacity-days">
                  {daysSummary.map(day => {
                    const dateObj = new Date(day.date + 'T00:00:00');
                    const dateLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    const effectiveCapacity = day.overriddenCapacity ?? day.defaultCapacity;
                    const totalBlocked = (day.blocks || []).reduce((s, b) => s + b.batchesBlocked, 0);
                    const usedByOrders = day.batchesUsed ?? 0;
                    const totalUsed = usedByOrders + totalBlocked;
                    const remaining = Math.max(0, effectiveCapacity - totalUsed);
                    const pct = Math.min(totalUsed / effectiveCapacity, 1);
                    const isExpanded = expandedDay === day.date;

                    return (
                      <div key={day.date} className="capacity-day-row">
                        {/* Day row */}
                        <div className="capacity-day-row__inner">
                          {/* Date + fill bar */}
                          <div>
                            <div className="capacity-day__label">{dateLabel}</div>
                            <div className="capacity-bar-track">
                              <div className={`capacity-bar-fill${pct >= 1 ? ' capacity-bar-fill--full' : pct >= 0.75 ? ' capacity-bar-fill--high' : ' capacity-bar-fill--ok'}`} style={{ width: `${pct * 100}%` }} />
                            </div>
                            <div className="capacity-day__remaining">{remaining.toFixed(2)} batch{remaining !== 1 ? 'es' : ''} free</div>
                          </div>

                          {/* Capacity override input */}
                          <div className="capacity-controls">
                            <div className="capacity-cap-row">
                              <span className="capacity-cap-label">Cap:</span>
                              <input
                                type="number"
                                min="1"
                                value={overrideInputs[day.date] ?? ''}
                                placeholder={String(day.defaultCapacity)}
                                onChange={e => setOverrideInputs(p => ({ ...p, [day.date]: e.target.value }))}
                                className={`capacity-input${day.overriddenCapacity != null ? ' capacity-input--overridden' : ''}`}
                              />
                              <button
                                onClick={() => handleSaveOverride(day.date)}
                                disabled={savingOverride[day.date]}
                                className="btn-admin-primary" style={{ padding: '5px 12px', fontSize: '12px' }}>
                                {savingOverride[day.date] ? '...' : 'Set'}
                              </button>
                              {day.overriddenCapacity != null && (
                                <button onClick={() => handleRemoveOverride(day.date)} title="Clear override" className="btn-clear-override">✕</button>
                              )}
                            </div>

                            {/* Blocks */}
                            <div className="capacity-blocks-row">
                              {(day.blocks || []).map(block => (
                                <span key={block.id} className="capacity-block-tag">
                                  🚫 {block.label} ({block.batchesBlocked}b)
                                  <button onClick={() => handleDeleteBlock(block.id)} className="capacity-block-tag__remove">×</button>
                                </span>
                              ))}
                              <button onClick={() => { setBlockModal(day.date); setBlockForm({ label: '', batchesBlocked: 1 }); }}
                                className="capacity-add-block-btn">
                                + Block
                              </button>
                            </div>
                          </div>

                          {/* Orders toggle */}
                          <button
                            onClick={() => setExpandedDay(isExpanded ? null : day.date)}
                            className="capacity-toggle-btn">
                            {(day.orders || []).length > 0
                              ? <span className="capacity-orders-badge">📋 {day.orders.length} order{day.orders.length !== 1 ? 's' : ''} {isExpanded ? '▲' : '▼'}</span>
                              : <span className="capacity-empty-badge">No orders</span>
                            }
                          </button>
                        </div>

                        {/* Expanded orders */}
                        {isExpanded && (day.orders || []).length > 0 && (
                          <div className="capacity-orders-expanded">
                            <div className="capacity-order-list">
                              {day.orders.map(order => (
                                <div key={order.id} className="capacity-order-item">
                                  <div>
                                    <span className="capacity-order-item__name">{order.customerName}</span>
                                    <span className="capacity-order-item__email">{order.customerEmail}</span>
                                  </div>
                                  <div className="capacity-order-item__meta">
                                    <span className={`badge-fulfillment badge-fulfillment--${order.fulfillmentType}`}>
                                      {order.fulfillmentType === 'shipping' ? '📦 Ship' : '🚗 Local'}
                                    </span>
                                    <span className="capacity-order-item__amount">${order.totalAmount?.toFixed(2)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add block modal */}
              {blockModal && (
                <div className="admin-modal-overlay">
                  <div className="admin-modal-body">
                    <h3 className="admin-modal-title">🚫 Block Capacity</h3>
                    <p className="admin-modal-subtitle">
                      {new Date(blockModal + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                    <div className="admin-modal-fields">
                      <div>
                        <label className="label-caps">Label (e.g. "Reserved for event")</label>
                        <input type="text" value={blockForm.label} onChange={e => setBlockForm(p => ({...p, label: e.target.value}))}
                          placeholder="e.g. Reserved for event"
                          className="admin-input" style={{ marginTop: '5px' }} autoFocus />
                      </div>
                      <div>
                        <label className="label-caps">Batches to block</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                          <button onClick={() => setBlockForm(p => ({...p, batchesBlocked: Math.max(1, p.batchesBlocked - 1)}))} className="modal-stepper-btn">−</button>
                          <span className="modal-stepper-display">{blockForm.batchesBlocked}</span>
                          <button onClick={() => setBlockForm(p => ({...p, batchesBlocked: p.batchesBlocked + 1}))} className="modal-stepper-btn">+</button>
                        </div>
                      </div>
                      <div className="admin-modal-actions">
                        <button onClick={handleAddBlock} className="btn-admin-primary" style={{ flex: 1 }}>Add Block</button>
                        <button onClick={() => setBlockModal(null)} className="btn-cancel">Cancel</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════ */}
          {/* TAB: ORDERS                                */}
          {/* ══════════════════════════════════════════ */}
          {activeTab === 'orders' && (
            <div>
              <div className="orders-header">
                <h3 className="orders-header__title">All Orders</h3>
                <button onClick={() => fetchAllOrders(adminToken)} className="btn-admin-primary" style={{ padding: '8px 16px' }}>↻ Refresh</button>
              </div>

              {/* Filter & search bar */}
              <div className="orders-filter-bar">
                <input
                  type="text"
                  placeholder="Search name, email, phone, or #order"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="filter-input" style={{ flex: '1 1 220px' }}
                />
                <select value={orderFilters.status} onChange={e => setOrderFilters(p => ({ ...p, status: e.target.value }))} className="filter-input">
                  <option value="ALL">All statuses</option>
                  <option value="PENDING_PAYMENT">Pending Payment</option>
                  <option value="FULFILLMENT">Fulfillment</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
                <select value={orderFilters.fulfillmentType} onChange={e => setOrderFilters(p => ({ ...p, fulfillmentType: e.target.value }))} className="filter-input">
                  <option value="ALL">All fulfillment</option>
                  <option value="local">Local Delivery</option>
                  <option value="shipping">Shipping</option>
                </select>
                <input type="date" title="From date" value={orderFilters.dateFrom} onChange={e => setOrderFilters(p => ({ ...p, dateFrom: e.target.value }))} className="filter-input" />
                <input type="date" title="To date" value={orderFilters.dateTo} onChange={e => setOrderFilters(p => ({ ...p, dateTo: e.target.value }))} className="filter-input" />
                <select value={orderFilters.sort} onChange={e => setOrderFilters(p => ({ ...p, sort: e.target.value }))} className="filter-input">
                  <option value="date_desc">Newest first</option>
                  <option value="date_asc">Oldest first</option>
                  <option value="amount_desc">Highest total</option>
                  <option value="amount_asc">Lowest total</option>
                </select>
                {hasActiveOrderFilters && (
                  <button onClick={clearOrderFilters} className="btn-admin-light">Clear filters</button>
                )}
              </div>

              {!ordersLoading && allOrders.length > 0 && (
                <div className="orders-count">
                  Showing {allOrders.length} of {ordersTotalCount}
                </div>
              )}

              {ordersLoading ? (
                <p className="loading-text">Loading orders...</p>
              ) : allOrders.length === 0 ? (
                <div className="orders-empty">
                  <p className="orders-empty__msg">
                    {hasActiveOrderFilters ? 'No orders match your filters.' : 'No orders yet.'}
                  </p>
                  {hasActiveOrderFilters && (
                    <button onClick={clearOrderFilters} className="btn-admin-light" style={{ marginTop: '12px' }}>Clear filters</button>
                  )}
                </div>
              ) : (
                <>
                  <div className="orders-list">
                    {allOrders.map(order => (
                      <div key={order.id} className="order-row">
                        <div className="order-row__inner">
                          <div className="order-row__left">
                            <div className="order-row__header">
                              <span className="order-row__name">{order.customerName}</span>
                              <span className={`badge-fulfillment badge-fulfillment--${order.fulfillmentType}`}>
                                {order.fulfillmentType === 'shipping' ? '📦 Shipping' : '🚗 Local Delivery'}
                              </span>
                              <span className={`status-badge status-badge--${order.orderStatus}`}>
                                {STATUS_CONFIG[order.orderStatus]?.label || order.orderStatus || 'Pending Payment'}
                              </span>
                            </div>
                            <div className="order-row__meta">{order.customerEmail} {order.customerPhone && `· ${order.customerPhone}`}</div>
                            <div className="order-row__date">
                              📅 {order.pickupDate} · {order.deliveryAddress || 'No address'}
                            </div>
                            {order.comments && <div className="order-row__comment">💬 {order.comments}</div>}
							{order.paymentMethod && (
							  <div className="order-row__payment">
								💳 {order.paymentMethod === 'CASH' ? 'Cash on delivery'
								   : order.paymentMethod === 'CARD' ? 'Credit/Debit card'
								   : order.paymentMethod === 'VENMO' ? 'Venmo'
								   : order.paymentMethod}
							  </div>
							)}
                            <button onClick={() => toggleExpandOrder(order.id)} className="expand-toggle">
                              {order.itemCount ?? 0} item{order.itemCount === 1 ? '' : 's'} {expandedOrderId === order.id ? '▲' : '▼'}
                            </button>
                          </div>
                          <div className="order-row__right">
                            <div className="order-row__amount-block">
                              <div className="order-row__amount">${order.totalAmount?.toFixed(2)}</div>
                              <div className="order-row__id">#{order.id}</div>
                              {order.couponCode && order.discountAmount > 0 && (
                                <div className="order-row__coupon">Coupon {order.couponCode}: −${order.discountAmount.toFixed(2)}</div>
                              )}
                            </div>
                            <div className="order-row__actions">
                              {STATUS_CONFIG[order.orderStatus]?.next && (
                                <button
                                  disabled={!!statusUpdating[order.id]}
                                  onClick={() => handleUpdateOrderStatus(order.id, STATUS_CONFIG[order.orderStatus].next)}
                                  className="btn-admin-ghost">
                                  {statusUpdating[order.id] ? '...' : STATUS_CONFIG[order.orderStatus].nextLabel}
                                </button>
                              )}
                              {['PENDING_PAYMENT', 'FULFILLMENT'].includes(order.orderStatus) && (
                                <button
                                  disabled={!!statusUpdating[order.id]}
                                  onClick={() => handleUpdateOrderStatus(order.id, 'CANCELLED')}
                                  className="btn-admin-danger">
                                  Cancel
                                </button>
                              )}
                              {['PENDING_PAYMENT', 'COMPLETED', 'CANCELLED'].includes(order.orderStatus) && (
                                <button
                                  disabled={!!orderDeleting[order.id]}
                                  onClick={() => handleDeleteOrder(order.id)}
                                  className="btn-admin-danger">
                                  {orderDeleting[order.id] ? '...' : '🗑 Delete'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded line items */}
                        {expandedOrderId === order.id && (
                          <div className="order-line-items">
                            {orderItemsLoading[order.id] ? (
                              <p className="product-uploading-msg">Loading items...</p>
                            ) : (orderItemsById[order.id] || []).length === 0 ? (
                              <p className="orders-empty__msg" style={{ fontSize: '13px' }}>No items found for this order.</p>
                            ) : (
                              (orderItemsById[order.id] || []).map(item => (
                                <div key={item.id} className="order-line-item">
                                  <div>
                                    <span className="order-line-item__name">{item.quantity}× {item.itemName} ({item.size})</span>
                                    {item.customizations?.length > 0 && (
                                      <div className="order-line-item__customizations">
                                        {item.customizations.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                  <span className="order-line-item__price">${item.lineTotal?.toFixed(2)}</span>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {ordersHasMore && (
                    <div className="orders-load-more-wrap">
                      <button onClick={handleLoadMoreOrders} disabled={ordersLoadingMore} className="btn-admin-primary">
                        {ordersLoadingMore ? 'Loading...' : `Load More (${allOrders.length} of ${ordersTotalCount})`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════ */}
          {/* TAB: SETTINGS                              */}
          {/* ══════════════════════════════════════════ */}
          {activeTab === 'settings' && (
            <div>
              <h3 className="section-title" style={{ marginBottom: '24px' }}>App Settings</h3>
              {appSettings.length === 0 ? (
                <p className="loading-text">Loading settings...</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '600px' }}>
                  {appSettings.map(s => (
                    <div key={s.settingKey}>
                      <label className="label-caps">
                        {s.settingKey === 'WELCOME_CAMPAIGN_ENABLED' ? 'Welcome Campaign' :
                         s.settingKey === 'STANDBY_MODE_ENABLED' ? 'Standby Mode' : s.settingKey}
                      </label>
                      {s.settingKey === 'WELCOME_CAMPAIGN_ENABLED' ? (
                        <p style={{ margin: '4px 0 6px', fontSize: '13px', color: '#666' }}>Show grand opening popup and enable first-order surprises</p>
                      ) : s.settingKey === 'STANDBY_MODE_ENABLED' ? (
                        <p style={{ margin: '4px 0 6px', fontSize: '13px', color: '#666' }}>Hide weekday date selection on the storefront and cap weekend capacity at 1 (unless a manual override or block already exists for that date)</p>
                      ) : (
                        s.description && <p style={{ margin: '4px 0 6px', fontSize: '13px', color: '#666' }}>{s.description}</p>
                      )}
                      {s.settingKey === 'BATCH_TO_SIZES_KEY' && (
                        <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#999', fontFamily: 'monospace' }}>Format: S_40_5,L_21_3,K_14_1</p>
                      )}
                      {(s.settingKey === 'WELCOME_CAMPAIGN_ENABLED' || s.settingKey === 'STANDBY_MODE_ENABLED') ? (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={(settingsEdits[s.settingKey] ?? s.settingValue) === 'true'}
                              onChange={e => {
                                const value = e.target.checked ? 'true' : 'false';
                                setSettingsEdits(prev => ({ ...prev, [s.settingKey]: value }));
                                saveSetting(s.settingKey, value);
                              }}
                            />
                            <span className="toggle-switch__slider" />
                          </label>
                          <span style={{ fontSize: '13px', color: '#666' }}>
                            {(settingsEdits[s.settingKey] ?? s.settingValue) === 'true' ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            className="admin-input"
                            value={settingsEdits[s.settingKey] ?? s.settingValue}
                            onChange={e => setSettingsEdits(prev => ({ ...prev, [s.settingKey]: e.target.value }))}
                            style={{ flex: 1 }}
                          />
                          <button onClick={() => saveSetting(s.settingKey)} className="btn-admin-primary" style={{ whiteSpace: 'nowrap' }}>Save</button>
                        </div>
                      )}
                      {s.updatedBy && (
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#aaa' }}>
                          Last updated by {s.updatedBy}{s.updatedAt ? ` · ${new Date(s.updatedAt).toLocaleString()}` : ''}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════ */}
          {/* TAB: COUPONS                               */}
          {/* ══════════════════════════════════════════ */}
          {activeTab === 'coupons' && (
            <div className="coupons-grid">
              <div>
                {editingCoupon ? (
                  <form onSubmit={handleEditCoupon} className="coupon-edit-form">
                    <h3 className="admin-section-heading">✏️ Edit Coupon</h3>
                    <div>
                      <label className="label-caps">Code</label>
                      <input type="text" required value={editingCoupon.code}
                        onChange={e => setEditingCoupon({ ...editingCoupon, code: e.target.value })}
                        className="admin-input" style={{ textTransform: 'uppercase' }} />
                    </div>
                    <div>
                      <label className="label-caps">Discount Type</label>
                      <select value={editingCoupon.discountType}
                        onChange={e => setEditingCoupon({ ...editingCoupon, discountType: e.target.value })}
                        className="admin-input">
                        <option value="PERCENTAGE">Percentage</option>
                        <option value="FIXED">Fixed Amount</option>
                      </select>
                    </div>
                    <div>
                      <label className="label-caps">{editingCoupon.discountType === 'PERCENTAGE' ? 'Discount (%)' : 'Discount ($)'}</label>
                      <input type="number" step="0.01" required value={editingCoupon.discountValue}
                        onChange={e => setEditingCoupon({ ...editingCoupon, discountValue: e.target.value })}
                        className="admin-input" />
                    </div>
                    <div>
                      <label className="label-caps">Min Order Amount</label>
                      <input type="number" step="0.01" value={editingCoupon.minOrderAmount ?? ''}
                        onChange={e => setEditingCoupon({ ...editingCoupon, minOrderAmount: e.target.value })}
                        placeholder="No minimum" className="admin-input" />
                    </div>
                    {editingCoupon.discountType === 'PERCENTAGE' && (
                      <div>
                        <label className="label-caps">Max Discount Cap</label>
                        <input type="number" step="0.01" value={editingCoupon.maxDiscountAmount ?? ''}
                          onChange={e => setEditingCoupon({ ...editingCoupon, maxDiscountAmount: e.target.value })}
                          placeholder="No cap" className="admin-input" />
                      </div>
                    )}
                    <div>
                      <label className="label-caps">Max Uses</label>
                      <input type="number" value={editingCoupon.maxUses ?? ''}
                        onChange={e => setEditingCoupon({ ...editingCoupon, maxUses: e.target.value })}
                        placeholder="Unlimited" className="admin-input" />
                    </div>
                    <div>
                      <label className="label-caps">Expiration Date</label>
                      <input type="datetime-local" value={editingCoupon.expiresAt ?? ''}
                        onChange={e => setEditingCoupon({ ...editingCoupon, expiresAt: e.target.value })}
                        className="admin-input" />
                    </div>
                    <label className="radio-label">
                      <input type="checkbox" checked={editingCoupon.singleUsePerEmail}
                        onChange={e => setEditingCoupon({ ...editingCoupon, singleUsePerEmail: e.target.checked })} />
                      Single Use Per Email
                    </label>
                    <label className="radio-label">
                      <input type="checkbox" checked={editingCoupon.active}
                        onChange={e => setEditingCoupon({ ...editingCoupon, active: e.target.checked })} />
                      Active
                    </label>
                    <div className="save-cancel-row">
                      <button type="submit" className="btn-save">Save Changes</button>
                      <button type="button" onClick={() => setEditingCoupon(null)} className="btn-cancel">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleAddCoupon} className="coupon-add-form">
                    <h3 className="admin-section-heading">➕ Add Coupon</h3>
                    <div>
                      <label className="label-caps">Code</label>
                      <input type="text" required placeholder="e.g. WELCOME10" value={newCoupon.code}
                        onChange={e => setNewCoupon({ ...newCoupon, code: e.target.value })}
                        className="admin-input" style={{ textTransform: 'uppercase' }} />
                    </div>
                    <div>
                      <label className="label-caps">Discount Type</label>
                      <select value={newCoupon.discountType}
                        onChange={e => setNewCoupon({ ...newCoupon, discountType: e.target.value })}
                        className="admin-input">
                        <option value="PERCENTAGE">Percentage</option>
                        <option value="FIXED">Fixed Amount</option>
                      </select>
                    </div>
                    <div>
                      <label className="label-caps">{newCoupon.discountType === 'PERCENTAGE' ? 'Discount (%)' : 'Discount ($)'}</label>
                      <input type="number" step="0.01" required value={newCoupon.discountValue}
                        onChange={e => setNewCoupon({ ...newCoupon, discountValue: e.target.value })}
                        placeholder={newCoupon.discountType === 'PERCENTAGE' ? '10' : '5.00'} className="admin-input" />
                    </div>
                    <div>
                      <label className="label-caps">Min Order Amount</label>
                      <input type="number" step="0.01" value={newCoupon.minOrderAmount}
                        onChange={e => setNewCoupon({ ...newCoupon, minOrderAmount: e.target.value })}
                        placeholder="No minimum" className="admin-input" />
                    </div>
                    {newCoupon.discountType === 'PERCENTAGE' && (
                      <div>
                        <label className="label-caps">Max Discount Cap</label>
                        <input type="number" step="0.01" value={newCoupon.maxDiscountAmount}
                          onChange={e => setNewCoupon({ ...newCoupon, maxDiscountAmount: e.target.value })}
                          placeholder="No cap" className="admin-input" />
                      </div>
                    )}
                    <div>
                      <label className="label-caps">Max Uses</label>
                      <input type="number" value={newCoupon.maxUses}
                        onChange={e => setNewCoupon({ ...newCoupon, maxUses: e.target.value })}
                        placeholder="Unlimited" className="admin-input" />
                    </div>
                    <div>
                      <label className="label-caps">Expiration Date</label>
                      <input type="datetime-local" value={newCoupon.expiresAt}
                        onChange={e => setNewCoupon({ ...newCoupon, expiresAt: e.target.value })}
                        className="admin-input" />
                    </div>
                    <label className="radio-label">
                      <input type="checkbox" checked={newCoupon.singleUsePerEmail}
                        onChange={e => setNewCoupon({ ...newCoupon, singleUsePerEmail: e.target.checked })} />
                      Single Use Per Email
                    </label>
                    <label className="radio-label">
                      <input type="checkbox" checked={newCoupon.active}
                        onChange={e => setNewCoupon({ ...newCoupon, active: e.target.checked })} />
                      Active
                    </label>
                    <button type="submit" className="btn-admin-primary" style={{ width: '100%', padding: '12px' }}>Add Coupon 🏷️</button>
                  </form>
                )}
              </div>
              <div className="coupons-list">
                <h3 className="products-list__title">Active Coupons ({allCoupons.length})</h3>
                <div className="products-list__items">
                  {allCoupons.map(c => {
                    const isExpired = c.expiresAt && new Date(c.expiresAt) < new Date();
                    const isMaxed = c.maxUses != null && c.currentUses >= c.maxUses;
                    const isExpanded = expandedCouponId === c.id;
                    return (
                      <div key={c.id} className="admin-coupon-row">
                        <div className="admin-coupon-row__inner">
                          <div className="admin-coupon-row__info">
                            <div className="admin-coupon-row__header">
                              <span className="admin-coupon-row__code">{c.code}</span>
                              <span className={`badge-coupon-type badge-coupon-type--${c.discountType.toLowerCase()}`}>
                                {c.discountType}
                              </span>
                              <span className="admin-coupon-row__value">
                                {c.discountType === 'PERCENTAGE' ? `${c.discountValue}%` : `$${c.discountValue.toFixed(2)}`}
                              </span>
                              {!c.active && <span className="badge-coupon-status badge-coupon-status--inactive">Inactive</span>}
                              {isExpired && <span className="badge-coupon-status badge-coupon-status--expired">Expired</span>}
                              {isMaxed && <span className="badge-coupon-status badge-coupon-status--maxed">Maxed</span>}
                            </div>
                            <div className="admin-coupon-row__meta">
                              Usage: {c.currentUses}/{c.maxUses ?? '∞'}
                              {c.minOrderAmount != null && ` · Min: $${c.minOrderAmount.toFixed(2)}`}
                              {' · '}{c.expiresAt ? new Date(c.expiresAt).toLocaleString() : 'No expiration'}
                            </div>
                            <button onClick={() => toggleExpandCoupon(c.id)} className="expand-toggle">
                              📋 Redemptions {isExpanded ? '▲' : '▼'}
                            </button>
                          </div>
                          <div className="product-row__actions-row">
                            <button
                              onClick={() => setEditingCoupon({ ...c, expiresAt: c.expiresAt ? c.expiresAt.slice(0, 16) : '' })}
                              className="btn-admin-ghost">Edit</button>
                            <button onClick={() => handleDeleteCoupon(c.id)} className="btn-admin-danger">Delete</button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="coupon-redemptions-panel">
                            {redemptionsLoading ? (
                              <p className="product-uploading-msg">Loading...</p>
                            ) : couponRedemptions.length === 0 ? (
                              <p className="orders-empty__msg" style={{ fontSize: '13px' }}>No redemptions yet</p>
                            ) : (
                              couponRedemptions.map(r => (
                                <div key={r.id} className="coupon-redemption-item">
                                  <div>
                                    <span className="coupon-redemption-item__email">{r.customerEmail}</span>
                                    <span className="coupon-redemption-item__order"> · Order #{r.orderId}</span>
                                  </div>
                                  <div className="coupon-redemption-item__meta">
                                    <span className="coupon-redemption-item__amount">${r.discountApplied.toFixed(2)}</span>
                                    <span className="coupon-redemption-item__date">{new Date(r.redeemedAt).toLocaleString()}</span>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}